import { FormikHelpers, getIn, useFormik } from 'formik'
import { CatalogProduct, CatalogProductInput } from '../types'
import * as Yup from 'yup'
import {
  isRequired,
  mustBeBoolean,
  mustBeLessThanCharacters,
  mustBeNumber,
  mustBeString,
  translateValidationErrors
} from '../utils/validation'
import {
  isAuroraCatalogProduct,
  isAxiomaW1CatalogProduct,
  isElectricalCabinetCatalogProduct,
  isGeneratorCatalogProduct,
  isHeaterCatalogProduct,
  isSpineLoadCatalogProduct,
  isSpineModuleCatalogProduct,
  isSpineMultiMeasurementCatalogProduct,
  isValveCatalogProduct
} from '../utils/catalogProduct'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import axios from 'axios'
import { useEffect, useState } from 'react'
import catalogProductService from '../services/catalogProductService'
import { createCatalogProduct, updateCatalogProduct } from '../redux/slices/catalog'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import { usePolicy } from '../contexts/PolicyContext'
import { AxiomaW1NominalDiameter, CatalogProductStatus, CatalogProductType } from '../enums'

export interface CatalogProductFormProps {
  /**
   * The type of a new catalog product. Resolved from the catalog product when editing.
   */
  type?: CatalogProductType

  /**
   * The catalog product to update. A new catalog product is created when not provided.
   */
  catalogProduct?: CatalogProduct
  afterSubmit?: (catalogProduct: CatalogProduct) => void
  afterError?: (error: unknown) => void
}

export function useCatalogProductForm({
  type,
  catalogProduct,
  afterSubmit,
  afterError
}: CatalogProductFormProps) {
  const { t } = useTranslation('catalogProduct')

  const dispatch = useDispatch()

  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * The photo to upload after the catalog product is saved.
   */
  const [photo, setPhoto] = useState<File | null>(null)

  /**
   * The type of the catalog product. Electrical cabinet is used until a type is selected.
   */
  const resolvedType = catalogProduct?.type ?? type ?? CatalogProductType.ELECTRICAL_CABINET

  // Edit the stored custom data, otherwise start from the defaults of the type.
  let customData: CatalogProductInput['customData']

  switch (resolvedType) {
    case CatalogProductType.ELECTRICAL_CABINET:
      customData =
        catalogProduct && isElectricalCabinetCatalogProduct(catalogProduct)
          ? {
              ...catalogProduct.customData,
              nominalCurrent:
                catalogProduct.customData?.nominalCurrent ?? catalogProduct.customData?.fuse
            }
          : {}
      break
    case CatalogProductType.SPINE_MODULE:
      customData =
        catalogProduct && isSpineModuleCatalogProduct(catalogProduct)
          ? // Keep the other stored custom data, the form only edits the spine config.
            {
              ...catalogProduct.customData,
              spine_config: catalogProduct.customData?.spine_config ?? {}
            }
          : { spine_config: {} }
      break
    case CatalogProductType.SPINE_LOAD: {
      const spineLoadCustomData =
        catalogProduct && isSpineLoadCatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : undefined

      customData = {
        spine_config: spineLoadCustomData?.spine_config ?? {},
        ...(spineLoadCustomData?.electricalCabinet && {
          electricalCabinet: {
            catalogProductId: spineLoadCustomData.electricalCabinet.catalogProductId ?? undefined
          }
        }),
        ...(spineLoadCustomData?.generator && {
          generator: {
            catalogProductId: spineLoadCustomData.generator.catalogProductId ?? undefined
          }
        })
      }
      break
    }
    case CatalogProductType.SPINE_MULTI_MEASUREMENT:
      customData =
        catalogProduct && isSpineMultiMeasurementCatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : {
              shipAsInstallationKit: false,
              outputs: { '1': { nominalCurrent: 100, clampRatio: 1000 } }
            }
      break
    case CatalogProductType.HEATER:
      customData =
        catalogProduct && isHeaterCatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : { heatingPower: 0 }
      break
    case CatalogProductType.GENERATOR:
      customData =
        catalogProduct && isGeneratorCatalogProduct(catalogProduct) ? catalogProduct.customData : {}
      break
    case CatalogProductType.AURORA:
      customData =
        catalogProduct && isAuroraCatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : { heatingPower: 0 }
      break
    case CatalogProductType.AXIOMA_W1:
      customData =
        catalogProduct && isAxiomaW1CatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : { nominalDiameter: AxiomaW1NominalDiameter.DN25 }
      break
    case CatalogProductType.VALVE: {
      const valveCustomData =
        catalogProduct && isValveCatalogProduct(catalogProduct)
          ? catalogProduct.customData
          : undefined

      // Keep only the ids of the linked catalog products.
      customData = {
        spine_config: {
          ...valveCustomData?.spine_config,
          // Default to single phase.
          single_phase: valveCustomData?.spine_config?.single_phase ?? 1
        },
        ...(valveCustomData?.electricalCabinet && {
          electricalCabinet: {
            catalogProductId: valveCustomData.electricalCabinet.catalogProductId ?? undefined
          }
        }),
        ...(valveCustomData?.heater && {
          heater: { catalogProductId: valveCustomData.heater.catalogProductId ?? undefined }
        }),
        ...(valveCustomData?.inRuuviTag && {
          inRuuviTag: {
            catalogProductId: valveCustomData.inRuuviTag.catalogProductId ?? undefined
          }
        }),
        ...(valveCustomData?.outRuuviTag && {
          outRuuviTag: {
            catalogProductId: valveCustomData.outRuuviTag.catalogProductId ?? undefined
          }
        })
      }
      break
    }
    default:
      // Stored custom data fits the input shape, the type has no custom data fields.
      customData = (catalogProduct?.customData as CatalogProductInput['customData']) ?? {}
  }

  const initialValues: CatalogProductInput = {
    name: catalogProduct?.name ?? '',
    description: catalogProduct?.description ?? '',
    manufacturer: catalogProduct?.manufacturer ?? '',
    manufacturerProductCode: catalogProduct?.manufacturerProductCode ?? '',
    // New catalog products are verified when created by a superadmin or a manufacturer.
    status:
      catalogProduct?.status ??
      (isSuperadmin || isManufacturer
        ? CatalogProductStatus.VERIFIED
        : CatalogProductStatus.UNVERIFIED),
    type: resolvedType,
    customData
  }

  const validationSchema = Yup.object({
    name: Yup.string()
      .typeError(mustBeString(t('name')))
      .required(isRequired(t('name'))),
    manufacturer: Yup.string()
      .typeError(mustBeString(t('manufacturer')))
      .max(255, mustBeLessThanCharacters(t('manufacturer'), 255))
      .required(isRequired(t('manufacturer'))),
    manufacturerProductCode: Yup.string()
      .typeError(mustBeString(t('manufacturerProductCode')))
      .max(255, mustBeLessThanCharacters(t('manufacturerProductCode'), 255))
      .nullable(),
    customData: Yup.object({
      ...(resolvedType === CatalogProductType.ELECTRICAL_CABINET && {
        nominalCurrent: Yup.number()
          .typeError(mustBeNumber(t('nominalCurrent')))
          .required(isRequired(t('nominalCurrent'))),
        clampRatio: Yup.number().typeError(mustBeNumber(t('clampRatio'))),
        inputCables: Yup.number().typeError(mustBeNumber(t('inputCables'))),
        isSinglePhase: Yup.boolean().typeError(mustBeBoolean(t('isSinglePhase'))),
        isMainDistributionAssembly: Yup.boolean().typeError(
          mustBeBoolean(t('isMainDistributionAssembly'))
        )
      }),
      ...(resolvedType === CatalogProductType.HEATER && {
        heatingPower: Yup.number()
          .typeError(mustBeNumber(t('heatingPower')))
          .required(isRequired(t('heatingPower')))
      }),
      ...(resolvedType === CatalogProductType.AXIOMA_W1 && {
        nominalDiameter: Yup.number()
          .typeError(mustBeNumber(t('nominalDiameter')))
          .required(isRequired(t('nominalDiameter')))
          .oneOf(
            Object.values(AxiomaW1NominalDiameter).filter(
              (diameter): diameter is AxiomaW1NominalDiameter => typeof diameter === 'number'
            )
          )
      })
    })
  })

  const { errors, touched, setFieldError, ...formik } = useFormik<CatalogProductInput>({
    initialValues,
    validationSchema,
    // Reset the form when the type or the catalog product changes.
    enableReinitialize: true,
    onSubmit: handleSubmit
  })

  function isError(field: string) {
    const fieldError = getIn(errors, field)
    const fieldTouched = getIn(touched, field)

    return Boolean(fieldError && fieldTouched)
  }

  function getHelperText(field: string) {
    const fieldError = getIn(errors, field)
    const fieldTouched = getIn(touched, field)

    return fieldTouched && fieldError ? fieldError : undefined
  }

  /**
   * Upload the selected photo for the saved catalog product.
   */
  async function uploadPhoto(savedCatalogProduct: CatalogProduct) {
    return photo !== null
      ? catalogProductService.uploadCatalogProductPhoto(savedCatalogProduct.id, photo)
      : savedCatalogProduct
  }

  /**
   * Create or update the catalog product.
   */
  async function handleSubmit(
    values: CatalogProductInput,
    { setSubmitting }: FormikHelpers<CatalogProductInput>
  ) {
    try {
      setSubmitting(true)

      if (catalogProduct) {
        const updatedCatalogProduct = await uploadPhoto(
          await catalogProductService.updateCatalogProduct(catalogProduct.id, values)
        )

        dispatch(updateCatalogProduct(updatedCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductUpdateSuccess')))

        afterSubmit?.(updatedCatalogProduct)
      } else {
        const createdCatalogProduct = await uploadPhoto(
          await catalogProductService.createCatalogProduct(values)
        )

        dispatch(createCatalogProduct(createdCatalogProduct))
        dispatch(createSuccessNotification(t('catalogProductCreateSuccess')))

        afterSubmit?.(createdCatalogProduct)
      }
    } catch (error) {
      if (
        axios.isAxiosError(error) &&
        error.response?.status === 422 &&
        typeof error.response.data?.validation === 'object'
      ) {
        const validationErrors = translateValidationErrors(error.response.data.validation)

        Object.keys(validationErrors).forEach((field) => {
          const fieldError = validationErrors[field]

          if (Array.isArray(fieldError) && typeof fieldError[0] === 'string') {
            setFieldError(field, fieldError[0])
          } else if (typeof fieldError === 'string') {
            setFieldError(field, fieldError)
          }
        })
      }

      dispatch(
        createErrorNotification(
          catalogProduct ? t('updateCatalogProductError') : t('createCatalogProductError')
        )
      )

      afterError?.(error)
    } finally {
      setSubmitting(false)
    }
  }

  return {
    touched,
    errors,
    setFieldError,
    ...formik,
    photo,
    setPhoto,
    isError,
    getHelperText
  }
}

export type CatalogProductLink = {
  catalogProductId?: string | null
  catalogProduct?: CatalogProduct | null
}

/**
 * Resolve a linked catalog product, loading it by id when not included.
 */
export function useLinkedCatalogProduct(link?: CatalogProductLink): CatalogProduct | null {
  const [loadedCatalogProduct, setLoadedCatalogProduct] = useState<CatalogProduct | null>(null)

  const catalogProductId = link?.catalogProductId ?? null

  const includedCatalogProduct = link?.catalogProduct ?? null

  useEffect(() => {
    if (includedCatalogProduct || !catalogProductId) {
      return
    }

    const controller = new AbortController()

    catalogProductService
      .getCatalogProduct(catalogProductId, { signal: controller.signal })
      .then(setLoadedCatalogProduct)
      .catch(() => {
        // Ignore the error.
      })

    return () => controller.abort()
  }, [catalogProductId, includedCatalogProduct])

  if (includedCatalogProduct) {
    return includedCatalogProduct
  }

  // Ignore a previously loaded catalog product until the current one is loaded.
  return loadedCatalogProduct?.id === catalogProductId ? loadedCatalogProduct : null
}
